import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';

export default function CalNotification() {
  const { fiId } = useParams<{ fiId: string }>();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  // Auto-fading toast surfaced when the draft can't be generated.
  const [showToast, setShowToast] = useState(false);

  const { data: fi, loading: fiLoading, error: fiError } = useApi<any>(() => API.getFI(fiId!), [fiId]);
  const { data: draft, loading: dLoading, error: dError } = useApi<any>(() => API.calDraft(fiId!), [fiId]);

  const draftFailed = !dLoading && (!!dError || !draft);
  // A missing CAL is the common, recoverable case — detect it so we can point
  // the user at the FI profile to add one rather than showing a dead error.
  const noCal = /no cal/i.test(dError ?? '');

  useEffect(() => {
    if (!draftFailed) return;
    setShowToast(true);
    const t = setTimeout(() => setShowToast(false), 5000);
    return () => clearTimeout(t);
  }, [draftFailed]);

  if (fiLoading || dLoading) return <div className="phil-loading">Loading…</div>;

  // If even the FI itself failed to load there's nothing to anchor to.
  if (fiError || !fi) {
    return <div className="phil-error">Unable to load CAL notification. {fiError}</div>;
  }

  if (draftFailed) {
    return (
      <>
        {showToast && (
          <div className="cal-toast" role="status">
            {noCal ? 'No CAL on file for this FI.' : (dError || 'Unable to generate the CAL notification.')}
          </div>
        )}
        <div className="breadcrumb">
          <a onClick={() => navigate('/feedback-priorities')}>Feedback &amp; Priorities</a>
          <span className="bc-sep">›</span>
          <a onClick={() => navigate(`/fi/${fi.id}`)}>{fi.name}</a>
          <span className="bc-sep">›</span>
          <span>CAL Notification</span>
        </div>
        <div className="page-header">
          <h1>Upcoming Client Meeting Email — {fi.name}</h1>
        </div>
        <div className="phil-error" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          <div>
            {noCal
              ? <>No <strong>Client Account Lead (CAL)</strong> is on file for {fi.name}, so this email can't be drafted. Add one on the FI profile.</>
              : <>Unable to generate the CAL notification. {dError}</>}
          </div>
          {noCal && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/fi/${fi.id}`)}>
              Add a CAL on {fi.name}’s profile →
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="breadcrumb">
        <a onClick={() => navigate('/feedback-priorities')}>Feedback &amp; Priorities</a>
        <span className="bc-sep">›</span>
        <a onClick={() => navigate(`/fi/${fi.id}`)}>{fi.name}</a>
        <span className="bc-sep">›</span>
        <span>CAL Notification</span>
      </div>

      <div className="page-header">

        <h1>Upcoming Client Meeting Email — {fi.name}</h1>
        <p className="subtitle">Sent 1 week before each Priority Call — one-pager attached as PDF (pp. 1–2 + client slide).</p>
      </div>

      <div className="email-box">
        <div className="email-field">
          <span className="email-label">From:</span>
          <span className="email-val">{draft.from}</span>
        </div>
        <div className="email-field">
          <span className="email-label">To:</span>
          <span className="email-val">{draft.to}</span>
        </div>
        <div className="email-field">
          <span className="email-label">Subject:</span>
          <span className="email-val" style={{ fontWeight: 500 }}>{draft.subject}</span>
        </div>
        <div className="email-body">{draft.body}</div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#374151' }}>
            📄 {draft.attachment}
          </div>
        </div>
        <div className="email-footer">
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/email-templates')}>✎ Edit</button>
          <button
            className={`btn btn-sm ${sent ? 'btn-outline' : 'btn-success'}`}
            onClick={() => { API.calSend(draft.draftId).catch(() => {}); setSent(true); }}
            style={{ marginLeft: 'auto' }}
          >
            {sent ? '✓ Sent' : '✓ Approve & Send'}
          </button>
        </div>
      </div>

      {sent && (
        <div style={{ marginTop: 10, padding: '10px 14px', background: '#dcfce7', borderRadius: 7, fontSize: 13, color: '#166534' }}>
          ✓ CAL notification sent to {fi.cal.email}
        </div>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/fi/${fi.id}/schedule-call`)}>← Schedule Priority Call</button>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/calls/${fi.id}/review`)}>Add Call Transcript →</button>
      </div>
    </>
  );
}
